import { CdkScrollableModule } from '@angular/cdk/scrolling';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	HostListener,
	inject,
	Input,
	NgModule,
	OnDestroy,
	OnInit,
	Output,
	Renderer2,
	viewChild,
} from '@angular/core';

import { OnboardingService } from '~app-client/onboarding/services';
import { OnboardingCondition } from '~app-client/onboarding/types';
import { ReachPanEvent, ReachPanModule } from '~app-ui/cdk/reach-pan';
import { blurActiveElement } from '~app-ui/cdk/utils';

@Component({
	// eslint-disable-next-line @angular-eslint/prefer-standalone
	standalone: false,

	selector: 'reach-bottom-menu',
	templateUrl: './bottom-menu.component.html',
	styleUrls: ['./bottom-menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReachBottomMenuComponent implements OnInit, AfterViewInit, OnDestroy {
	private readonly onboardingService = inject(OnboardingService);
	private readonly elementRef = inject(ElementRef<HTMLElement>);
	private readonly document = inject(DOCUMENT);
	private readonly renderer = inject(Renderer2);
	private readonly cdRef = inject(ChangeDetectorRef);

	public _emitToOnboarding = true;

	@Input()
	public lightBg = false;

	@Input()
	public transitionTimeMs = 800;

	@HostBinding('class.fullscreen')
	@Input()
	public fullscreen = false;

	@HostBinding('class.bg-dark')
	@Input()
	public darkBg = false;

	@HostBinding('class.full-height')
	@Input()
	public fullHeight = false;

	@HostBinding('class.apple-style-header')
	@Input()
	public appleStyleHeader = false;

	@HostBinding('style.--transition-time.ms')
	public get partialTransitionTimeMs(): number {
		const menuHeight = this.$menu()?.nativeElement.getBoundingClientRect().height ?? 0;
		const height = this.nativeElement.getBoundingClientRect().height;

		const menuTop = menuHeight - this.verticalDisplacementPx;
		const time = (menuTop * this.transitionTimeMs) / height;

		return Math.max(this.transitionTimeMs / 2, time);
	}

	@HostBinding('class.hide-right-option')
	@Input()
	public hideRightOption = false;

	@Input()
	public openOnStartup = false;

	@HostBinding('class.hide-left-option')
	@Input()
	public hideLeftOption = false;

	@Input()
	public closeOnBackground = true;

	@Input()
	public canSwipeDown = true;

	@Input()
	public showHeader = true;

	@Input()
	public showFooter = false;

	@Output('closed')
	public closed$$ = new EventEmitter<void>();

	@Output('opened')
	public opened$$ = new EventEmitter<void>();

	@HostBinding('style.--left-size.px')
	public get leftSizePx(): number {
		return Math.ceil(this.$leftOption()?.nativeElement.getBoundingClientRect().width ?? 0);
	}

	@HostBinding('style.--right-size.px')
	public get rightSizePx(): number {
		return Math.ceil(this.$rightOption()?.nativeElement.getBoundingClientRect().width ?? 0);
	}

	@HostBinding('style.--header-padding.px')
	public get maxPaddingPx(): number {
		return Math.max(this.leftSizePx, this.rightSizePx);
	}

	private readonly $leftOption = viewChild<ElementRef<HTMLElement>>('leftOption');
	private readonly $rightOption = viewChild<ElementRef<HTMLElement>>('rightOption');
	private readonly $menu = viewChild<ElementRef<HTMLElement>>('menu');

	@HostBinding('style.--displacement.px')
	public verticalDisplacementPx = 0;

	@HostBinding('class.opened')
	public fullyOpened = false;

	@HostBinding('class.fully-closed')
	public fullyClosed = true;

	@HostBinding('class.opening')
	public opening = false;

	@HostBinding('class.closing')
	public closing = false;

	@HostBinding('style.--__opacity-scale')
	public opacityScale = 1;

	@HostBinding('class.will-close')
	public willClose = false;

	@HostBinding('class.animating')
	public animating = false;

	private animationTimer?: number;

	public get reachPanActive(): boolean {
		return this.canSwipeDown && !this.animating;
	}

	private _onboardingConditionAdded = false;

	public get nativeElement(): HTMLElement {
		return this.elementRef.nativeElement as HTMLElement;
	}

	ngOnInit(): void {
		this.renderer.appendChild(this.document.body, this.nativeElement);
	}

	ngAfterViewInit(): void {
		if (this.openOnStartup) {
			this.open();
		}
	}

	ngOnDestroy(): void {
		this.renderer.removeChild(this.document.body, this.nativeElement);
	}

	public panMove(event: ReachPanEvent): void {
		this.setDisplacement(event.point.y);
	}

	public panFinish(event: ReachPanEvent): void {
		this.setDisplacement(event.point.y);

		if (this.willClose) {
			this.close();
		} else {
			this.panCancel();
		}
	}

	public panCancel(): void {
		if (this.verticalDisplacementPx > 0) {
			this.animate(() => {
				this.setDisplacement(0);
			});
		}
	}

	@HostListener('click', ['$event'])
	public onBackgroundClick(event?: Event): void {
		const close = this.closeOnBackground && !event?.defaultPrevented;

		event?.preventDefault();
		event?.stopPropagation();

		if (close) {
			this.close();
		}
	}

	public close(): void {
		if (this.fullyOpened) {
			this.animate(() => {
				this.fullyOpened = false;
				this.closing = true;
			});
		}
	}

	public open(): void {
		if (this.fullyClosed) {
			this.animate(() => {
				blurActiveElement(this.document);

				this.opening = true;
				this.fullyClosed = false;
			});
		}
	}

	public toggle(): void {
		if (this.fullyOpened) {
			this.close();
		} else if (this.fullyClosed) {
			this.open();
		}
	}

	public onTransitionEnd(event: TransitionEvent, force = false): void {
		const target = event.target as HTMLElement & { parent: HTMLElement };

		if (
			(force || target === this.elementRef.nativeElement) &&
			event.propertyName === 'transform'
		) {
			window.clearTimeout(this.animationTimer);
			this.animating = false;

			if (this.closing) {
				this.onFullyClosed();
			} else {
				this.onFullyOpened();
			}
		}
	}

	public stopEvent(event?: Event): void {
		event?.stopPropagation();
	}

	public updateUI(): void {
		this.cdRef.markForCheck();
	}

	private setDisplacement(displacement: number): void {
		if (this._onboardingConditionAdded) {
			this.onboardingService.removeCondition(OnboardingCondition.GENERIC_MENU_FULLY_OPENED);
			this._onboardingConditionAdded = false;
		}

		this.verticalDisplacementPx = Math.min(
			this.nativeElement.getBoundingClientRect().height,
			Math.max(0, displacement)
		);

		const height = this.$menu()?.nativeElement.getBoundingClientRect().height ?? 0;
		const currentTop = height - this.verticalDisplacementPx;

		const percent = currentTop / height;

		const willClose = percent < 0.5 || this.verticalDisplacementPx > 60;

		if (!this.willClose && willClose) {
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			window?.navigator?.vibrate?.(30);
		}

		this.willClose = willClose;
	}

	private animate(cb: () => void): void {
		this.animating = true;
		requestAnimationFrame(() => {
			cb();
			this.animationTimer = window.setTimeout(() => {
				this.onTransitionEnd(new TransitionEvent('', { propertyName: 'transform' }), true);
			}, this.partialTransitionTimeMs + 50);
			this.updateUI();
		});
	}

	private onFullyClosed(): void {
		if (!this.fullyClosed) {
			this.closing = false;
			this.fullyClosed = true;

			if (this._onboardingConditionAdded) {
				this.onboardingService.removeCondition(
					OnboardingCondition.GENERIC_MENU_FULLY_OPENED
				);
				this._onboardingConditionAdded = false;
			}

			this.verticalDisplacementPx = 0;
			this.willClose = false;

			this.closed$$.emit();

			blurActiveElement(this.document);

			this.cdRef.detectChanges();
		}
	}

	private onFullyOpened(): void {
		if (!this.fullyOpened) {
			this.opening = false;
			this.fullyOpened = true;

			if (this._emitToOnboarding) {
				this.onboardingService.addCondition(OnboardingCondition.GENERIC_MENU_FULLY_OPENED);
				this._onboardingConditionAdded = true;
			}

			this.verticalDisplacementPx = 0;
			this.willClose = false;

			this.opened$$.emit();

			blurActiveElement(this.document);

			this.cdRef.detectChanges();
		}
	}
}

@NgModule({
	declarations: [ReachBottomMenuComponent],
	imports: [CommonModule, ReachPanModule, CdkScrollableModule],
	exports: [ReachBottomMenuComponent],
})
export class ReachBottomMenuModule {}
